app.service('TyroAPI', [
	'$http',
	'ENV',
	'Session',
	function ($http, ENV, Session) {

		var service = {
			isProcessing: false,

			getAccessToken: function (data) {
				return $http({
					method: "POST",
					url: ENV.API + "/api/tyro/token",
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					}
				});
			},
			// Reference data for the claim-item drop-down: CatTrax SYSTEM items (no fee) plus the practice's
			// own PRACTICE items, which carry its fee. Returned whole, so callers filter client side.
			//
			// practiceId names the practice to read as, for the settings screen, where a user who belongs to
			// several practices edits one that is not the one they are working in today. Omitted everywhere
			// else, which reads as the practice in session.
			getClaimableItems: function (itemType, practiceId) {
				return $http({
					method: "GET",
					url: ENV.API + "/api/tyro/claimable-items",
					params: itemType ? { item_type: itemType } : {},
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId || Session.practice.id}`,
					}
				});
			},
			// The code may duplicate a SYSTEM item's, which is how a practice attaches its own pricing.
			createClaimableItem: function (data, practiceId) {
				return $http({
					method: "POST",
					url: ENV.API + "/api/tyro/claimable-items",
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId || Session.practice.id}`,
					},
					data: data,
				});
			},
			// Practice items only - the server rejects SYSTEM items as read-only reference data.
			updateClaimableItem: function (tyroClaimableItemId, data, practiceId) {
				return $http({
					method: "PUT",
					url: ENV.API + "/api/tyro/claimable-items/" + tyroClaimableItemId,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId || Session.practice.id}`,
					},
					data: data,
				});
			},
			// Practice items only. Items already recorded against a patient keep their snapshotted code,
			// name and fee, so this does not rewrite what has been claimed.
			deleteClaimableItem: function (tyroClaimableItemId, practiceId) {
				return $http({
					method: "DELETE",
					url: ENV.API + "/api/tyro/claimable-items/" + tyroClaimableItemId,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId || Session.practice.id}`,
					}
				});
			},
			// Items recorded against a patient during their care, each snapshotting the code, name, funder and
			// fee it was recorded with. A claimed item is marked CLAIMED (then PAID) and kept rather than
			// deleted, so pass 'PENDING' for the ones still waiting to be claimed.
			getPatientClaimableItems: function (patientId, status) {
				return $http({
					method: "GET",
					url: ENV.API + "/api/tyro/claimable-items/patient/" + patientId,
					params: status ? { status: status } : {},
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					}
				});
			},
			// Records an item, a comment, or both. Re-adding an item already on the record refreshes its
			// snapshot rather than adding a second row; a comment always stands as a row of its own, as
			// there is nothing to key one consult against the next by.
			addPatientClaimableItem: function (tyroClaimableItemId, patientId, comment) {
				return $http({
					method: "POST",
					url: ENV.API + "/api/tyro/claimable-items/patient/" + patientId,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
					data: {
						tyro_claimable_item_id: tyroClaimableItemId || null,
						comment: comment || null,
					},
				});
			},
			// Puts an item code onto a row a clinician left as a comment, which is how an administrator
			// decides what "initial consult" actually bills as. Only a pending row can be changed.
			// Omitting a field leaves it as it is.
			updatePatientClaimableItem: function (patientClaimableItemId, patientId, tyroClaimableItemId, comment) {
				return $http({
					method: "PUT",
					url: ENV.API + "/api/tyro/claimable-items/patient/" + patientId + "/" + patientClaimableItemId,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
					data: {
						tyro_claimable_item_id: tyroClaimableItemId || null,
						comment: comment === undefined ? null : comment,
					},
				});
			},
			removePatientClaimableItem: function (tyroClaimableItemId, patientId) {
				return $http({
					method: "DELETE",
					url: ENV.API + "/api/tyro/claimable-items/" + tyroClaimableItemId + "/patient/" + patientId,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					}
				});
			},
			createTransaction: function (data) {
				return $http({
					method: "POST",
					url: ENV.API + "/api/tyro/transaction",
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
					data: data,
				});
			},
			// Raises an invoice straight onto the patient, with no funder in it: private work, a non-claimable
			// item, a fee nobody is going to claim. The server raises the Xero invoice and records it as a
			// PATIENT_INVOICE transaction naming it, so it comes back on the claims list and is paid the same
			// way a claim's gap is. Any claimable_item_ids sent are taken off the pending list with it.
			createPatientInvoice: function (patientId, data) {
				return $http({
					method: "POST",
					url: ENV.API + "/api/tyro/transaction/patient/" + patientId + "/invoice",
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
					data: data,
				});
			},
			// ECLIPSE OPVW: synchronous check of the Medicare and PHI details held on the patient record.
			// Persists and lodges nothing - it reports any details Medicare suggests correcting.
			verifyEclipsePatient: function (data) {
				return $http({
					method: "POST",
					url: ENV.API + "/api/tyro/eclipse/verify",
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
					data: data,
				});
			},
			// Checks the Medicare, DVA and health fund details typed into the patient form, before they are
			// saved. Each funder answers separately; nothing is persisted.
			verifyPatientCover: function (data) {
				return $http({
					method: "POST",
					url: ENV.API + "/api/tyro/eclipse/patient-cover/verify",
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
					data: data,
				});
			},
			// ECLIPSE Phase 1 (OECW): synchronous eligibility quote, served by the backend calling the
			// Medipass ECLIPSE API rather than the SDK. Lodges nothing. The backend persists the quote as an
			// ECLIPSE_ELIGIBILITY transaction and returns its transaction_id, so callers must NOT create one.
			checkEclipseEligibility: function (data) {
				return $http({
					method: "POST",
					url: ENV.API + "/api/tyro/eclipse/eligibility",
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
					data: data,
				});
			},
			// ECLIPSE Phase 2 (IMCW): promotes a persisted quote to an in-patient claim and lodges it,
			// reusing the same transaction row. The post-op path - no form re-entry, no second transaction.
			// The claim then settles asynchronously by webhook.
			//
			// accountPaid is the one thing the quote cannot carry: whether the patient has settled the account
			// is only known after the surgery, and the quote is taken before it. Sent only when the caller has
			// an answer, so a lodgement that has none leaves the stored row as it stands.
			submitEclipseEligibilityClaim: function (transactionId, patientId, accountPaid) {
				return $http({
					method: "POST",
					url: ENV.API + "/api/tyro/eclipse/transaction/" + transactionId + "/patient/" + patientId + "/submit",
					params: (accountPaid === true || accountPaid === false) ? { accountPaid: accountPaid } : {},
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					}
				});
			},
			// Corrects the claimant on a quote that has not been lodged, without running a new eligibility
			// check. The recovery path for a claim ECLIPSE refused over its claimant after the patient had
			// already paid: a new check would record a new quote with no invoice, and the account would be
			// raised and charged a second time.
			updateQuoteClaimant: function (transactionId, patientId, claimant) {
				return $http({
					method: "PUT",
					url: ENV.API + "/api/tyro/eclipse/transaction/" + transactionId + "/patient/" + patientId + "/claimant",
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
					data: claimant,
				});
			},
			// Every reason a quote cannot be lodged right now, without lodging it. Read as the lodgement step
			// opens so a claim that cannot go says so before the patient is charged - which matters most on a
			// quote finished from the claims list, where the claim is lodged from the stored row and anything
			// missing from that row is not on the screen to see.
			getLodgementIssues: function (transactionId, patientId) {
				return $http({
					method: "GET",
					url: ENV.API + "/api/tyro/eclipse/transaction/" + transactionId + "/patient/" + patientId + "/lodgement-issues",
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					}
				});
			},
			// ECLIPSE patient (PC) claim only: raises and finalises the Xero invoice for a quote before the claim
			// is lodged, so the patient's account can be settled while they are still at the desk. Every other
			// class is invoiced by lodgement instead - the funder settles to the practice on those, so there is
			// nothing for the patient to pay. Idempotent: returns the existing invoice if one was already raised.
			raisePatientClaimInvoice: function (transactionId, patientId) {
				return $http({
					method: "POST",
					url: ENV.API + "/api/tyro/eclipse/transaction/" + transactionId + "/patient/" + patientId + "/invoice",
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					}
				});
			},
			// Voids the invoice above for a quote that is not going to be lodged, so leaving the screen does not
			// strand a live invoice on the patient's account. Refused by the server once anything has been paid
			// against it - that money is the practice's and the record of it is the patient's receipt.
			discardPatientClaimInvoice: function (transactionId, patientId) {
				return $http({
					method: "DELETE",
					url: ENV.API + "/api/tyro/eclipse/transaction/" + transactionId + "/patient/" + patientId + "/invoice",
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					}
				});
			},
			// ECLIPSE OVSW: lodges an overseas claim for a patient on overseas visitor (OVHC) or overseas
			// student (OSHC) cover. Persisted and lodged in one call - overseas claiming has no eligibility
			// phase to promote from, because no part of the claim is processed by Medicare. The synchronous
			// response only means the claim reached the fund; its assessment arrives in a processing report.
			submitOverseasClaim: function (data) {
				return $http({
					method: "POST",
					url: ENV.API + "/api/tyro/eclipse/overseas",
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
					data: data,
				});
			},
			// Re-reads a lodged ECLIPSE claim's processing report - in-patient and overseas alike. The report
			// is published on the funders' schedule rather than by webhook, so this is the manual read
			// alongside the daily check. Safe to call repeatedly.
			refreshProcessingReport: function (transactionId, patientId) {
				return $http({
					method: "POST",
					url: ENV.API + "/api/tyro/eclipse/transaction/" + transactionId + "/patient/" + patientId + "/processing-report/refresh",
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					}
				});
			},
			// Downloads the same report as a PDF, which reads it server side on the way through - so a claim
			// is brought up to date by the download as well as by the refresh above.
			//
			// arraybuffer, not the default: a PDF read as a string is corrupted by the time it reaches the
			// Blob. The error path pays for it - a failure body arrives as bytes rather than as the parsed
			// JSON the interceptor would give us - which is why callers decode it before reporting it.
			downloadProcessingReport: function (transactionId, patientId) {
				return $http({
					method: "GET",
					url: ENV.API + "/api/tyro/eclipse/transaction/" + transactionId + "/patient/" + patientId + "/processing-report",
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					},
					responseType: "arraybuffer",
				});
			},
			deleteTransaction: function (transactionId, patientId) {
				return $http({
					method: "DELETE",
					url: ENV.API + "/api/tyro/transaction/" + transactionId + "/patient/" + patientId,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					}
				});
			},
			getPatientClaims: function (patientId) {
				return $http({
					method: "GET",
					url: ENV.API + "/api/tyro/transaction/patient/" + patientId,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${Session.practice.id}`,
					}
				});
			}
		};

		return service;
	},
]);
